
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supportService, SupportMessage } from '@/services/supportService';
import { useAuth } from '@/contexts/AuthContext';

interface SupportChatContextType {
  messages: SupportMessage[];
  isLoading: boolean;
  isSending: boolean;
  hasUnreadMessages: boolean;
  isOpen: boolean;
  sendMessage: (messageText: string) => Promise<void>;
  openChat: () => void;
  closeChat: () => void;
  fetchMessages: () => Promise<void>;
  markAsRead: () => void;
}

const SupportChatContext = createContext<SupportChatContextType | undefined>(undefined);

export const SupportChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();
  const lastMessageCountRef = useRef(0);
  const hasInitializedRef = useRef(false);

  // Check if user is currently on support page
  const isOnSupportPage = () => {
    return window.location.pathname === '/support';
  };

  // Fetch messages - centralized function
  const fetchMessages = async () => {
    if (!user || isLoading) return;
    
    try {
      setIsLoading(true);
      const fetchedMessages = await supportService.getMessages();
      
      // Check for new messages only if chat is closed AND user is not on support page
      if (!isOpen && !isOnSupportPage() && fetchedMessages.length > lastMessageCountRef.current) {
        setHasUnreadMessages(true);
      }
      
      // If user is on support page, always mark as read
      if (isOnSupportPage()) {
        setHasUnreadMessages(false);
      }
      
      setMessages(fetchedMessages);
      lastMessageCountRef.current = fetchedMessages.length;
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Send message
  const sendMessage = async (messageText: string) => {
    if (!user || !messageText.trim()) return;

    try {
      setIsSending(true);
      
      const newMessage: SupportMessage = {
        username: user.username || user.name,
        message: messageText.trim(),
        timestamp: new Date().toISOString()
      };

      // Add message immediately to the UI for instant feedback
      setMessages(prevMessages => [...prevMessages, newMessage]);
      
      const usernameToSend = user.username;
      
      const payload = {
        username: usernameToSend,
        message: messageText.trim(),
        timestamp: new Date().toISOString()
      };

      await supportService.sendMessage(payload);
      
    } catch (error) {
      console.error('Error sending message:', error);
      // Remove the optimistically added message if there was an error
      setMessages(prevMessages => prevMessages.slice(0, -1));
      throw error;
    } finally {
      setIsSending(false);
    }
  };

  // Open chat and mark as read
  const openChat = () => {
    setIsOpen(true);
    setHasUnreadMessages(false);
  };

  // Close chat
  const closeChat = () => {
    setIsOpen(false);
  };

  // Mark messages as read when viewing them
  const markAsRead = () => {
    setHasUnreadMessages(false);
  };

  // Initialize - only fetch messages once when user is available
  useEffect(() => {
    if (user && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      fetchMessages();
    }
  }, [user]);

  // Mark as read when chat is opened
  useEffect(() => {
    if (isOpen) {
      setHasUnreadMessages(false);
    }
  }, [isOpen]);

  // Clear notifications when user visits support page
  useEffect(() => {
    const handleLocationChange = () => {
      if (isOnSupportPage()) {
        setHasUnreadMessages(false);
      }
    };

    // Check immediately
    handleLocationChange();

    // Listen for route changes
    window.addEventListener('popstate', handleLocationChange);
    
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  const value: SupportChatContextType = {
    messages,
    isLoading,
    isSending,
    hasUnreadMessages,
    isOpen,
    sendMessage,
    openChat,
    closeChat,
    fetchMessages,
    markAsRead
  };

  return (
    <SupportChatContext.Provider value={value}>
      {children}
    </SupportChatContext.Provider>
  );
};

export const useSupportChatContext = () => {
  const context = useContext(SupportChatContext);
  if (context === undefined) {
    throw new Error('useSupportChatContext must be used within a SupportChatProvider');
  }
  return context;
};
